import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useNavigate } from "react-router-dom";
import { Check, Calendar, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$14.99",
    period: "/month",
    description: "1 pickup per month",
    priceId: "price_1SuSODPhLqfgxDXsLKU7rEBs",
    icon: Calendar,
    features: ["1 scheduled pickup monthly", "All carriers supported"],
  },
  {
    id: "bi-weekly",
    name: "Bi-Weekly",
    price: "$24.99",
    period: "/month",
    description: "2 pickups per month",
    priceId: "price_1SuSOXPhLqfgxDXsDwd31EXd",
    icon: Zap,
    popular: true,
    features: ["2 scheduled pickups monthly", "All carriers supported"],
  },
];

const Index = () => {
  const navigate = useNavigate();

  const handleSelectPlan = (planId: string, priceId: string) => {
    navigate(`/auth?plan=${planId}&priceId=${priceId}&tab=signup`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background via-background to-muted/30">
      <Navbar />
      <main className="flex-1">

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Returns Made Simple
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Doorstep Returns, Done for You.
              <br />
              Schedule convenient package pickups from your doorstep. No more trips to wait in lines.
            </p>
            <Button variant="hero" size="lg" className="text-lg px-8 py-6" onClick={() => navigate("/auth?tab=signup")}>
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row justify-center gap-6">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={cn(
                    "relative rounded-2xl border-2 p-6 transition-all cursor-pointer hover:border-primary/60 hover:shadow-xl hover:scale-[1.02] w-full max-w-sm mx-auto md:mx-0",
                    plan.popular
                      ? "border-primary bg-gradient-to-b from-primary/10 to-primary/5 shadow-lg"
                      : "border-border bg-card shadow-md",
                  )}
                  onClick={() => handleSelectPlan(plan.id, plan.priceId)}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-4">
                    <div
                      className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3",
                        plan.popular ? "bg-primary/20" : "bg-muted",
                      )}
                    >
                      <Icon className={cn("w-6 h-6", plan.popular ? "text-primary" : "text-muted-foreground")} />
                    </div>
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>

                  <div className="text-center mb-4">
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>

                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">Cancel anytime. No hidden fees.</p>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Sign Up</h3>
              <p className="text-muted-foreground">Create your account in seconds</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Schedule Pickup</h3>
              <p className="text-muted-foreground">Enter your package details</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">We Handle It</h3>
              <p className="text-muted-foreground">Relax while we pick up and process your return</p>
            </div>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default Index;
